import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  NO_OP_SINK,
  isAnalyticsDisabled,
  setAnalyticsSink,
  subscribeAnalyticsPreference,
  track,
} from "./index";
import { FirstPartySink } from "./FirstPartySink";

/**
 * Installs the analytics sink and records navigation. Renders nothing.
 *
 * Page views are counted here, from the router, rather than in each page's own
 * effect. Ten call sites that must each be remembered is ten chances for a new
 * screen to be invisible in the numbers, and the failure is silent — the page
 * simply looks unused. One subscription to the location cannot drift.
 *
 * Must be rendered inside the router, and above the routes.
 */
const Analytics: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [disabled, setDisabled] = useState(isAnalyticsDisabled);

  // The Settings switch writes the preference and dispatches an event; this
  // is what lets the running sink react to that immediately instead of only
  // on the next launch.
  useEffect(() => subscribeAnalyticsPreference(setDisabled), []);

  useEffect(() => {
    if (disabled) {
      // The resident asked for less to be sent, so the outgoing sink is
      // dropped without flushing whatever it was still holding.
      setAnalyticsSink(NO_OP_SINK);
      return;
    }

    // The language is read at send time rather than captured, so events sent
    // after a language change carry the language the resident is actually
    // reading.
    const sink = new FirstPartySink({ language: () => i18n.language });
    setAnalyticsSink(sink);
    const stop = sink.start();

    return () => {
      // Only flush when this cleanup runs for an ordinary reason (language
      // change, unmount). When it runs because the resident just opted out,
      // localStorage already reflects that, and sending the pending batch
      // would be exactly what they asked not to happen.
      if (!isAnalyticsDisabled()) {
        void sink.flush();
      }
      stop();
      setAnalyticsSink(NO_OP_SINK);
    };
  }, [i18n, disabled]);

  useEffect(() => {
    // The path only. A query string can carry what a resident typed into the
    // A–Z search, and that is theirs.
    track("page_view", { page: location.pathname });
  }, [location.pathname]);

  return null;
};

export default Analytics;
